import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { selectSearch } from 'src/store/selectors/search';
import { selectLoader } from 'src/store/selectors/loader';
import { setSearch } from 'src/store/actions/search';
import {
  fetchPropertyById,
  fetchPropertyCounts,
} from 'src/store/actions/properties';
import { PATHS, ICONS } from 'src/common/constants';
import {
  checkSparkDataAvailable,
  onRequestPhoneCall,
} from 'src/helpers/validationHelper';
import {
  ContactFormActionsContext,
  useContactFormActionsFromContext,
} from 'src/SharedCommonContext/ContactFormContext';
import { ITableColumn } from 'src/components/Table/types';
import { createPropertyDevelopersTableColumns } from 'src/components/PropertyDevelopersTable/helpers';
import { usePropertyDevelopersListOperations } from 'src/pages/SharedPageCustomHooks/UseContactsSavePropertiesPage/usePropertyDevelopersListOperations';
import { useContactAPIOperationsProperties } from 'src/pages/SharedPageCustomHooks/UseContactsSavePropertiesPage/useContactAPIOperationsProperties';
import {
  FetchContactByIdResult,
  useContactAPI,
  UseContactAPIReturn,
} from 'src/SharedCustomHooks/UseContactAPI/useContactAPI';
import { PropertyManagerRow } from 'src/pages/SharedPageCustomHooks/UseContactsSavePropertiesPage/types';
import { IContactFormData } from 'src/SharedCustomHooks/UseContactFormData/types';

export interface UsePropertyDevelopersPageOrchestratorReturn {
  propertyDevelopers: PropertyManagerRow[];
  propertyData: any;
  loading: boolean;
  globalLoaderVisible: boolean;
  showOverlay: boolean;
  editingExistingContactInTable: boolean;
  allowedContactFormRoles: string[];
  canAddContact: boolean;
  columns: ITableColumn[];
  searchIsActive: string;
  onSearch: (e: ChangeEvent<HTMLInputElement>) => void;
  onOpenAddContact: () => void;
  onEditContact: (contactId: string) => Promise<void>;
  onCloseContactForm: () => void;
  onSubmitContactForm: (
    formData: IContactFormData,
    formType: string
  ) => Promise<void>;
  onDeleteContact: (contact: PropertyManagerRow) => Promise<void>;
}

const AUTHORIZED_ROLES_FOR_ADD_CONTACT: string[] = [
  'Admin',
  'Super Admin',
  'Property Developer',
];

const DEFAULT_CONTACT_FORM_ROLES: string[] = [
  'Property Developer',
  'Service Manager',
];

export const usePropertyDevelopersPageOrchestrator = (
  propertyData: any
): UsePropertyDevelopersPageOrchestratorReturn => {
  const user = useSelector(selectAuthenticatedUser);
  const search = useSelector(selectSearch);
  const globalLoaderVisible: boolean = !!useSelector(selectLoader);
  const dispatch = useDispatch();

  // UI state. This is simple enough to have inline here for now but if it gets more complex extract into its own hook.
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [editingExistingContactInTable, setEditingExistingContactInTable] =
    useState<boolean>(false);
  const [editingContactRole, setEditingContactRole] = useState<string | null>(
    null
  );

  const {
    clearContactFormData,
    setContactFormData,
    initializeContactFormDataFromEdit,
  }: ContactFormActionsContext = useContactFormActionsFromContext();

  const {
    propertyDevelopers,
    isFetchingDevelopers,
    fetchPropertyDevelopers,
    isDeleting,
    deletePropertyDeveloperFromProperty,
  } = usePropertyDevelopersListOperations();

  const contactApi: UseContactAPIReturn = useContactAPI();

  const currentProperty: any = useMemo(() => {
    if (!propertyData) {
      return null;
    }

    return {
      propertyId: propertyData.id || propertyData.propertyId,
      id: propertyData.id || propertyData.propertyId,
      name:
        propertyData.name || propertyData.propertyName || 'Current Property',
    };
  }, [propertyData]);

  const propertyManagerId: string | undefined =
    propertyData?.propertyManager?.id;

  const userRoleName: string = user?.roles?.[0]?.role?.name;

  const isAuthorizedRoleForAddContact: boolean =
    AUTHORIZED_ROLES_FOR_ADD_CONTACT.includes(userRoleName);

  const isSparkDataAvailable: boolean = checkSparkDataAvailable(
    propertyData,
    userRoleName
  );

  const canAddContact: boolean =
    isAuthorizedRoleForAddContact && !isSparkDataAvailable;

  const allowedContactFormRoles: string[] =
    editingExistingContactInTable && editingContactRole
      ? [editingContactRole]
      : DEFAULT_CONTACT_FORM_ROLES;

  const searchIsActive: string = search?.contacts || '';

  useEffect(() => {
    if (!propertyData?.propertyId) {
      return;
    }

    void fetchPropertyDevelopers(propertyData.propertyId);
  }, [propertyData?.propertyId]);

  const onCloseContactForm = (): void => {
    setShowOverlay(false);
    clearContactFormData();
    setEditingExistingContactInTable(false);
    setEditingContactRole(null);
  };

  // TODO: phase N — replace fetchPropertyCounts thunk with property entity refetch via custom hook
  const refreshPropertyCounts = (): void => {
    dispatch(
      fetchPropertyCounts(propertyData.propertyId, user.id, userRoleName)
    );
  };

  const { handleSubmitContactForm: onSubmitContactForm } = useContactAPIOperationsProperties({
    propertyId: propertyData?.propertyId || propertyData?.id,
    adminContactId: propertyManagerId || user.id,
    editingExistingContactInTable,
    onSuccess: refreshPropertyCounts,
    onClose: onCloseContactForm,
    onRefreshDevelopers: fetchPropertyDevelopers,
  });

  const onOpenAddContact = (): void => {
    clearContactFormData();

    const initialProperties: any[] = currentProperty
      ? [
          {
            propertyId: currentProperty.propertyId || currentProperty.id,
            id: currentProperty.propertyId || currentProperty.id,
            name: currentProperty.name,
          },
        ]
      : [];

    setContactFormData({
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      type: 'default',
      userRole: 'Property Developer',
      isPrimaryContact: false,
      properties: initialProperties,
      propertyId: currentProperty?.propertyId,
    });

    setShowOverlay(true);
  };

  const onEditContact = async (contactId: string): Promise<void> => {
    setEditingExistingContactInTable(true);

    const result: FetchContactByIdResult =
      await contactApi.fetchContactById(contactId);

    if (!result.ok || !result.contact) {
      toast.error('🚫 Failed to load contact details. Please try again.', {
        autoClose: 4000,
        position: 'top-center',
      });

      setEditingExistingContactInTable(false);
      return;
    }

    const contact: any = result.contact;

    setEditingContactRole(contact.roles?.[0]?.role?.name || null);

    initializeContactFormDataFromEdit({
      ...contact,
      isPrimaryPropertyDeveloperContact: contact.isPrimaryContact,
    });

    setShowOverlay(true);
  };

  // TODO: phase N — replace fetchPropertyById + fetchPropertyCounts thunks with custom hook refetch
  const onDeleteContact = async (
    contact: PropertyManagerRow
  ): Promise<void> => {
    const result = await deletePropertyDeveloperFromProperty(
      contact.id,
      propertyData.propertyId
    );

    if (!result.success) {
      return;
    }

    dispatch(fetchPropertyById(propertyData.propertyId));
    refreshPropertyCounts();

    await fetchPropertyDevelopers(propertyData.propertyId);
  };

  const onSearch = (e: ChangeEvent<HTMLInputElement>): void => {
    dispatch(setSearch({ propertyDevelopers: e.target.value.trim() }));
  };

  const columns: ITableColumn[] = useMemo(
    () =>
      createPropertyDevelopersTableColumns({
        propertyData,
        user,
        handleSetContactFormData: onEditContact,
        onDeleteContact,
        PATHS,
        ICONS,
        onRequestPhoneCall,
        checkSparkDataAvailable,
      }),
    [propertyData, user]
  );

  const loading: boolean = isFetchingDevelopers || isDeleting;

  return {
    propertyDevelopers,
    propertyData,
    loading,
    globalLoaderVisible,
    showOverlay,
    editingExistingContactInTable,
    allowedContactFormRoles,
    canAddContact,
    columns,
    searchIsActive,
    onSearch,
    onOpenAddContact,
    onEditContact,
    onCloseContactForm,
    onSubmitContactForm,
    onDeleteContact,
  };
};
